import { useProjectStore } from '@/store/project-store'
import { ActionMenuTabs, useApplicationStore } from '@/store/application-store'
import useTextStates from '@/composables/UseTextStates'
import useImageStates from '@/composables/UseImageStates'
import { LogoState, StickerState, TextState } from '@/types'
import useIdGenerator from '@/composables/UseIdGenerator'

enum DeleteKeys {
  BACKGROUND = 'Backspace',
  DELETE = 'Delete'
}

export default function useCanvasEventHandler () {
  const applicationStore = useApplicationStore()
  const projectStore = useProjectStore()
  const { addText, deleteText, saveText } = useTextStates()
  const { deleteSticker } = useImageStates()
  const { getId } = useIdGenerator()

  const handleUserKeyPress = (event: KeyboardEvent): void => {
    const deleteKeys: string[] = Object.values(DeleteKeys)
    const deleteKeyPressed = deleteKeys.includes(event.key)
    const showTextEditOverlay = applicationStore.activeActionTab === ActionMenuTabs.TEXT && applicationStore.editTextState !== 'none'

    if (!deleteKeyPressed || showTextEditOverlay || event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return
    }

    event.preventDefault()

    if (applicationStore.selectedText !== null) {
      onDeleteText()
    }

    if (applicationStore.selectedSticker !== null) {
      onDeleteSticker()
    }
  }

  const handleCut = (event: Event): void => {
    if (applicationStore.activeActionTab !== ActionMenuTabs.TEXT || applicationStore.editTextState === 'none') {
      handleCopy(event)
      if (applicationStore.selectedText) {
        onDeleteText()
      } else if (applicationStore.selectedSticker) {
        onDeleteSticker()
      }
    } else {
      applicationStore.clipboardData = null
    }
  }

  const handleCopy = (event: Event): void => {
    const clipEvent = event as ClipboardEvent
    const selectedObject = applicationStore.selectedSticker ?? applicationStore.selectedText
    if (selectedObject && (applicationStore.activeActionTab !== ActionMenuTabs.TEXT || applicationStore.editTextState === 'none')) {
      applicationStore.clipboardData = selectedObject
      clipEvent.clipboardData?.setData('text/plain', '')
      event.preventDefault()
    } else {
      applicationStore.clipboardData = null
    }
  }

  const handlePaste = (event: Event): void => {
    const clipEvent = event as ClipboardEvent

    // Do not paste if user is currently editing text or has copied content from outside the canvas
    if ((clipEvent.clipboardData && clipEvent.clipboardData?.items.length > 0) || !applicationStore.clipboardData || (applicationStore.activeActionTab === ActionMenuTabs.TEXT && applicationStore.editTextState !== 'none')) {
      return
    }

    // Check if clipboard data is text, logo or sticker
    if ('text' in applicationStore.clipboardData) {
      // Text needs to go through add/save process to trigger rasterization
      const copiedTextState = applicationStore.clipboardData as TextState
      // Creates and stores new textState in currentEditingText
      addText(copiedTextState)
      if (!applicationStore.currentEditingText) {
        return
      }
      // Set the position of the new text to be at the centre of the canvas
      applicationStore.currentEditingText.x = projectStore.resolution.width / 2
      applicationStore.currentEditingText.y = projectStore.resolution.height / 2
      saveText(applicationStore.currentEditingText)
    } else if ('filename' in applicationStore.clipboardData) {
      const newStickerState = {
        ...applicationStore.clipboardData as LogoState,
        x: projectStore.resolution.width / 2,
        y: projectStore.resolution.height / 2,
        id: getId()
      }
      projectStore.logoStickers.push(newStickerState)
    } else {
      const newStickerState = {
        ...applicationStore.clipboardData as StickerState,
        x: projectStore.resolution.width / 2,
        y: projectStore.resolution.height / 2,
        id: getId()
      }
      projectStore.stickers.push(newStickerState)
    }
    clipEvent.preventDefault()
  }

  const onDeleteText = (): void => {
    if (applicationStore.selectedText) {
      deleteText(applicationStore.selectedText)
      if (projectStore.textStates.length === 0) {
        addText({ style: applicationStore.selectedText?.style })
      } else {
        applicationStore.selectedText = null
      }
    }
  }

  const onDeleteSticker = (): void => {
    if (applicationStore.selectedSticker) {
      deleteSticker(applicationStore.selectedSticker.id)
      applicationStore.selectedSticker = null
    }
  }

  return {
    handleUserKeyPress,
    handleCopy,
    handleCut,
    handlePaste,
    onDeleteText,
    onDeleteSticker
  }
}